import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** Format INR amount with lakh/crore grouping. */
export function formatINR(n: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(n);
}

/**
 * Convert a number into Indian-numbering words.
 * e.g., 930686 → "INR Nine Lakh Thirty Thousand Six Hundred Eighty Six Only"
 * Used for the invoice "Amount Chargeable (in words)" line.
 */
export function rupeesInWords(amount: number): string {
  const a = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen',
  ];
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  function inWords(num: number): string {
    if (num === 0) return '';
    if (num < 20) return a[num];
    if (num < 100) return b[Math.floor(num / 10)] + (num % 10 ? ' ' + a[num % 10] : '');
    if (num < 1000) return a[Math.floor(num / 100)] + ' Hundred' + (num % 100 ? ' ' + inWords(num % 100) : '');
    return '';
  }

  const rupees = Math.floor(amount);
  const paise = Math.round((amount - rupees) * 100);

  if (rupees === 0 && paise === 0) return 'INR Zero Only';

  const crore = Math.floor(rupees / 10000000);
  const lakh = Math.floor((rupees % 10000000) / 100000);
  const thousand = Math.floor((rupees % 100000) / 1000);
  const remainder = rupees % 1000;

  const parts: string[] = [];
  if (crore) parts.push(inWords(crore) + ' Crore');
  if (lakh) parts.push(inWords(lakh) + ' Lakh');
  if (thousand) parts.push(inWords(thousand) + ' Thousand');
  if (remainder) parts.push(inWords(remainder));

  let out = 'INR ' + parts.join(' ');
  if (paise) out += ' and ' + inWords(paise) + ' Paise';
  out += ' Only';
  return out;
}

/** Compose a GST-compliant sequential invoice number: ST/RS/26-27/0001 */
export function formatInvoiceNo(seq: number, fy: string = '26-27', state: string = 'RS', prefix: string = 'ST'): string {
  return `${prefix}/${state}/${fy}/${String(seq).padStart(4, '0')}`;
}

/**
 * Extract a human-readable message from any thrown thing.
 * Supabase PostgrestError is a plain object {message, code, details, hint},
 * not an Error — so `e instanceof Error` is false and `String(e)` gives '[object Object]'.
 */
export function errMsg(e: unknown): string {
  if (!e) return 'Unknown error';
  if (typeof e === 'string') return e;
  if (e instanceof Error) return e.message;
  if (typeof e === 'object') {
    const o = e as { message?: string; details?: string; hint?: string; code?: string };
    return o.message || o.details || o.hint || (o.code ? `Code ${o.code}` : JSON.stringify(e));
  }
  return String(e);
}
